import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography
} from '@mui/material';
import { ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { AnnouncementBar, AnnouncementItem, HeaderNavItem } from '../../types';

const createEmptyItem = (): HeaderNavItem => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  label: '',
  path: '/products',
  visible: true,
  position: 'left',
  parentId: null,
  order: 0,
  openInNewTab: false
});

const createAnnouncementItem = (): AnnouncementItem => ({
  id: `announce-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  text: '',
  visible: true,
  order: 0,
  link: null,
  openInNewTab: false
});

const shopifyStylePreset: HeaderNavItem[] = [
  { id: 'home', label: 'Home', path: '/', visible: true, position: 'left', parentId: null, order: 1 },
  { id: 'shop', label: 'Shop', path: '/products', visible: true, position: 'left', parentId: null, order: 2 },
  { id: 'earrings', label: 'Earrings', path: '/products?category=earrings', visible: true, position: 'left', parentId: 'shop', order: 3 },
  { id: 'necklaces', label: 'Necklaces', path: '/products?category=necklaces-pendants', visible: true, position: 'left', parentId: 'shop', order: 4 },
  { id: 'rings', label: 'Rings', path: '/products?category=rings', visible: true, position: 'left', parentId: 'shop', order: 5 },
  { id: 'under-599', label: 'Under ₹599', path: '/products?maxPrice=599', visible: true, position: 'left', parentId: null, order: 6 },
  { id: 'account', label: 'Account', path: '/account', visible: true, position: 'right', parentId: null, order: 7 }
];

const minimalistPreset: HeaderNavItem[] = [
  { id: 'home', label: 'Home', path: '/', visible: true, position: 'left', parentId: null, order: 1 },
  { id: 'collections', label: 'Collections', path: '/products', visible: true, position: 'left', parentId: null, order: 2 },
  { id: 'under-599', label: 'Under ₹599', path: '/products?maxPrice=599', visible: true, position: 'left', parentId: null, order: 3 },
  { id: 'account', label: 'Account', path: '/account', visible: true, position: 'right', parentId: null, order: 4 }
];

const AdminNavigationPage: React.FC = () => {
  const [items, setItems] = useState<HeaderNavItem[]>([]);
  const [announcementBar, setAnnouncementBar] = useState<AnnouncementBar>({
    enabled: true,
    speedMs: 22000,
    items: []
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);

  const rootItems = useMemo(() => items.filter((item) => !item.parentId), [items]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [navRes, announcementRes] = await Promise.all([
          apiClient.get<{ items: HeaderNavItem[] }>(`${API_ENDPOINTS.SITE_SETTINGS}/header-nav`),
          apiClient.get<{ announcementBar: AnnouncementBar }>(
            `${API_ENDPOINTS.SITE_SETTINGS}/announcement-bar`
          )
        ]);
        setItems((navRes.data.items || []).sort((a, b) => a.order - b.order));
        if (announcementRes.data.announcementBar) {
          setAnnouncementBar({
            ...announcementRes.data.announcementBar,
            items: [...(announcementRes.data.announcementBar.items || [])].sort(
              (a, b) => a.order - b.order
            )
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const updateItem = (id: string, patch: Partial<HeaderNavItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const removeItem = (id: string) => {
    setItems((prev) =>
      prev
        .filter((item) => item.id !== id && item.parentId !== id)
        .map((item) => (item.parentId === id ? { ...item, parentId: null } : item))
    );
  };

  const addItem = () => {
    const maxOrder = items.reduce((max, item) => Math.max(max, item.order), 0);
    setItems((prev) => [...prev, { ...createEmptyItem(), order: maxOrder + 1 }]);
  };

  const normalizeOrders = (nextItems: HeaderNavItem[]) =>
    [...nextItems]
      .sort((a, b) => a.order - b.order)
      .map((item, index) => ({ ...item, order: index + 1 }));

  const moveItem = (id: string, direction: 'up' | 'down') => {
    setItems((prev) => {
      const sorted = normalizeOrders(prev);
      const index = sorted.findIndex((item) => item.id === id);
      if (index < 0) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return prev;
      const copy = [...sorted];
      [copy[index], copy[targetIndex]] = [copy[targetIndex], copy[index]];
      return normalizeOrders(copy);
    });
  };

  const applyPreset = (preset: HeaderNavItem[]) => {
    setItems(preset.map((item) => ({ ...item })));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put<{ items: HeaderNavItem[] }>(
        `${API_ENDPOINTS.SITE_SETTINGS}/header-nav`,
        {
        items: items.map((item) => ({
          ...item,
          parentId: item.parentId || null
        }))
      }
      );
      if (Array.isArray(data.items)) {
        setItems((data.items || []).sort((a, b) => a.order - b.order));
      }
      window.dispatchEvent(new Event('header-nav-updated'));
    } finally {
      setSaving(false);
    }
  };

  const updateAnnouncementItem = (id: string, patch: Partial<AnnouncementItem>) => {
    setAnnouncementBar((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.id === id ? { ...item, ...patch } : item))
    }));
  };

  const addAnnouncementItem = () => {
    const maxOrder = announcementBar.items.reduce((max, item) => Math.max(max, item.order), 0);
    setAnnouncementBar((prev) => ({
      ...prev,
      items: [...prev.items, { ...createAnnouncementItem(), order: maxOrder + 1 }]
    }));
  };

  const removeAnnouncementItem = (id: string) => {
    setAnnouncementBar((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id)
    }));
  };

  const moveAnnouncementItem = (id: string, direction: 'up' | 'down') => {
    setAnnouncementBar((prev) => {
      const sorted = [...prev.items].sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((item) => item.id === id);
      if (index < 0) return prev;
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return prev;
      const copy = [...sorted];
      [copy[index], copy[targetIndex]] = [copy[targetIndex], copy[index]];
      return {
        ...prev,
        items: copy.map((item, idx) => ({ ...item, order: idx + 1 }))
      };
    });
  };

  const saveAnnouncementBar = async () => {
    setSavingAnnouncement(true);
    try {
      const { data } = await apiClient.put<{ announcementBar: AnnouncementBar }>(
        `${API_ENDPOINTS.SITE_SETTINGS}/announcement-bar`,
        {
          announcementBar: {
            enabled: announcementBar.enabled,
            speedMs: announcementBar.speedMs,
            items: announcementBar.items.map((item, idx) => ({
              ...item,
              order: idx + 1,
              link: item.link || null
            }))
          }
        }
      );
      if (data.announcementBar) {
        setAnnouncementBar({
          ...data.announcementBar,
          items: [...(data.announcementBar.items || [])].sort((a, b) => a.order - b.order)
        });
      }
      window.dispatchEvent(new Event('announcement-bar-updated'));
    } finally {
      setSavingAnnouncement(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Navigation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control storefront header links dynamically. Parent items create dropdowns.
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <MuiButton variant="contained" onClick={addItem}>
          Add item
        </MuiButton>
        <MuiButton variant="outlined" onClick={save} disabled={saving || loading}>
          {saving ? 'Saving...' : 'Save navigation'}
        </MuiButton>
      </Box>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <MuiButton variant="text" onClick={() => applyPreset(shopifyStylePreset)}>
          Apply Shopify-style preset
        </MuiButton>
        <MuiButton variant="text" onClick={() => applyPreset(minimalistPreset)}>
          Apply minimalist preset
        </MuiButton>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1240 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 90 }}>ID</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Label</TableCell>
              <TableCell sx={{ minWidth: 300 }}>Path</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Position</TableCell>
              <TableCell sx={{ minWidth: 190 }}>Parent</TableCell>
              <TableCell sx={{ minWidth: 90 }}>Order</TableCell>
              <TableCell sx={{ minWidth: 80 }}>Visible</TableCell>
              <TableCell sx={{ minWidth: 90 }}>New Tab</TableCell>
              <TableCell align="center" sx={{ minWidth: 90 }}>
                Move
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 80 }}>
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {item.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={item.label}
                    onChange={(e) => updateItem(item.id, { label: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={item.path}
                    onChange={(e) => updateItem(item.id, { path: e.target.value })}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={item.position}
                    onChange={(e) =>
                      updateItem(item.id, { position: e.target.value as 'left' | 'right' })
                    }
                  >
                    <MenuItem value="left">Left</MenuItem>
                    <MenuItem value="right">Right</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={item.parentId || ''}
                    onChange={(e) => updateItem(item.id, { parentId: e.target.value || null })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {rootItems
                      .filter((root) => root.id !== item.id)
                      .map((root) => (
                        <MenuItem key={root.id} value={root.id}>
                          {root.label || root.id}
                        </MenuItem>
                      ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={item.order}
                    onChange={(e) =>
                      setItems((prev) =>
                        normalizeOrders(
                          prev.map((it) =>
                            it.id === item.id ? { ...it, order: Number(e.target.value) } : it
                          )
                        )
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.visible}
                        onChange={(e) => updateItem(item.id, { visible: e.target.checked })}
                      />
                    }
                    label=""
                  />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(item.openInNewTab)}
                        onChange={(e) =>
                          updateItem(item.id, { openInNewTab: e.target.checked })
                        }
                      />
                    }
                    label=""
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => moveItem(item.id, 'up')}>
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveItem(item.id, 'down')}>
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton color="error" onClick={() => removeItem(item.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper variant="outlined" sx={{ mt: 3, p: 2.5 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Announcement bar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage the top running banner messages shown on the storefront.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={announcementBar.enabled}
                onChange={(e) =>
                  setAnnouncementBar((prev) => ({ ...prev, enabled: e.target.checked }))
                }
              />
            }
            label="Enable announcement bar"
          />
          <TextField
            size="small"
            type="number"
            label="Ticker speed (ms)"
            value={announcementBar.speedMs}
            onChange={(e) =>
              setAnnouncementBar((prev) => ({
                ...prev,
                speedMs: Number(e.target.value) || 22000
              }))
            }
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
          <MuiButton variant="outlined" onClick={addAnnouncementItem}>
            Add message
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={saveAnnouncementBar}
            disabled={savingAnnouncement || loading}
          >
            {savingAnnouncement ? 'Saving...' : 'Save banner'}
          </MuiButton>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Message</TableCell>
                <TableCell sx={{ minWidth: 220 }}>Link (optional)</TableCell>
                <TableCell align="center">Visible</TableCell>
                <TableCell align="center">New tab</TableCell>
                <TableCell align="center">Move</TableCell>
                <TableCell align="right">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcementBar.items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.text}
                      onChange={(e) => updateAnnouncementItem(item.id, { text: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="/products?maxPrice=599"
                      value={item.link || ''}
                      onChange={(e) => updateAnnouncementItem(item.id, { link: e.target.value })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={item.visible}
                      onChange={(e) => updateAnnouncementItem(item.id, { visible: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={Boolean(item.openInNewTab)}
                      onChange={(e) =>
                        updateAnnouncementItem(item.id, { openInNewTab: e.target.checked })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => moveAnnouncementItem(item.id, 'up')}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveAnnouncementItem(item.id, 'down')}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton color="error" onClick={() => removeAnnouncementItem(item.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AdminNavigationPage;
